// @ts-strict-ignore
import {
    BuildDistributionBucketDownloadMetricRequest,
    GetAllDevicesRequest,
    OrgOverviewFilters,
    OrgSummaryViewEntityRequest,
    ReleaseStepEntityRequest,
    TryFetchEntityActionData,
    TryFetchEntityActionV2,
} from './../actions/entities';
import {
    BuildDistroBucketBase,
    ChecklistItemTemplate,
    OrgUsersVCSUserData,
    FetchBuildDistributionBucketBuildResponse,
    FetchBuildDistributionBucketBuildsResponse,
    FetchBuildDistributionBucketBuildsWorkItemsTimelineResponse,
    FetchBuildDistributionBucketDownloadMetricsResponse,
    FetchBuildDistributionBucketResponse,
    FetchBuildDistributionBucketsWithRecentBuildsResponse,
    FetchBuildDistributionSharedWithMeBuildsResponse,
    GetAllDevicesResponse,
    GetProvisioningProfilesResult,
    ReleaseStep,
    RunwayModelsResponse,
    RunwayResponseV2,
} from './../types';
import { fetchRolloutMonitoringSuccess, RolloutMonitoringResponse } from '../actions/rolloutMonitoring';
import {
    FetchFeatureFlagsResponse,
    fetchFeatureFlagsSuccess,
    TryUpdateFeatureFlagsAction,
} from '../actions/featureFlags';
import { fetchOrgOverviewV2Success, OrgOverviewV2Response } from './../actions/orgOverview';
import {
    createSummaryViewScopeKey,
    fetchOrgSummaryViewSuccess,
    OrgSummaryViewResponse,
} from './../actions/orgSummaryView';
import {
    fetchOrgDestinationsAndVersionsSuccess,
    OrgDestinationsAndVersionsResponse,
} from './../actions/orgDestinationsAndVersions';
import { SummaryViewResponseAPI, OrgDestinationsResponse } from './../types/summaryView';
import {
    AppSettingsUpdatable,
    AppSettingsV2Updatable,
    BuildDistributionUpdatable,
    ChecklistItemUpdatable,
    DestinationUpdatable,
    DevicesUpdatable,
    FeatureFlagUpdatable,
    OrgSettingsUpdatable,
    ReleaseStepUpdatable,
    ReleaseUpdatable,
} from '../actions/updatable';
import {
    AppHome,
    AppSettings,
    AuthCredentials,
    FeatureFlag,
    OrganizationEntity,
    Release,
    ReleaseStatistics,
    RolloutMonitoring,
} from '../types';
import * as API from '../api';
import { UpdatableEndpointResponse } from '../api';
import { BaseAppRequest } from '../actions/appSettings';
import {
    BaseUpdateReleaseRequest,
    ConnectedReleasesResponse,
    FetchReleaseResponse,
    fetchReleasesSuccessAction,
    fetchReleaseSuccess,
    TryUpdateReleaseAction,
} from '../actions/release';
import { fetchIssuesSuccessAction, IssuesRequest, IssuesResponse, updateIssuesPagination } from '../actions/triage';
import {
    AppEntityRequest,
    BuildDistributionBucketBuildsWorkItemsTimelineRequest,
    BuildDistributionBucketsWithRecentBuildsEntityRequest,
    BuildDistributionSharedWithMeBuildsEntityRequest,
    EntityRequest,
    EntityResponse,
    FetchEntitySuccessAction,
    FetchPaginatedEntitiesSuccessAction,
    FetchPaginatedEntitiesUpdatePaginationDataAction,
    OrgOverviewEntityRequest,
    PaginatedEntitiesRequest,
    PaginatedEntitiesResponse,
    ReleaseEntityRequest,
    ReleasesRequest,
    ReleasesResponse,
    TryFetchEntityAction,
    TryFetchPaginatedEntitiesAction,
} from '../actions/entities';
import { FetchableEntityType, FetchableEntityTypeV2, FetchablePaginatedEntityType } from '../entityType';
import { AppHomeResponse, fetchAppHomeSuccess } from '../actions/appHome';
import { AppSettingsV2APIResponse, ChecklistItemUpdatableAPIResponse } from '../apiResponseTypes';
import { BaseUpdateOrgSettingsRequest, SetDefaultGroupsDisabledResponse } from '../actions/orgSettings';
import { fetchReleaseStatsHistorySuccess, ReleaseStatsHistoryResponse } from '../actions/releaseStatsHistory';
import {
    updateAllDevicesPagination,
    updateBucketBuildsPagination,
    updateBucketBuildWorkItemsTimelinePagination,
    updateReleasesPagination,
    updateSharedWithMeBuildsPagination,
} from '../actions/ui';
import {
    fetchBuildDistributionBucketBuildsSuccess,
    fetchBuildDistributionBucketBuildSuccess,
    fetchBuildDistributionBucketDownloadMetricsSuccess,
    fetchBuildDistributionBucketSuccess,
    fetchBuildDistributionBucketsWithRecentBuildsSuccess,
    fetchBuildDistributionBuildWorkItemsTimelineSuccess,
    fetchBuildDistributionSharedWithMeBuildsSuccess,
    TryUpdateBuildDistributionBucketAction,
} from '../actions/buildDistribution';
import { fetchAllDevicesSuccess, fetchProvisioningProfilesSuccess } from '../reducers/apps';
import { TryUpdateDevicesAction } from '../actions/devices';
import {
    fetchReleaseStepSuccess,
    UpdateReleaseStepRequestDataTypes,
    UpdateReleaseStepResourceIds,
} from '@/actions/releaseStep';
import { TryUpdateChecklistItemAction } from '@/actions/checklistItems';
import { AppStateResponse, fetchAppStateSuccess } from '@/actions/app';
import { StepRun } from '@/types/stepRun';
import { RunwayUpdatable, UpdatableRequestDataType, UpdatableResourceIdsType } from '@/actions/common';
import { StepConfigUpdatable } from '@/actions/stepConfig';
import { StepRunUpdatable } from '@/actions/stepRun';
import { WorkflowUpdatable } from '@/actions/workflow';
import { StepConfig } from '@/types/stepConfig';
import { fetchReleaseOverviewSuccess, ReleaseOverviewResponse } from '@/actions/releaseOverview';
import { Nullable } from '@/typescriptHelpers';
import { WorkflowOverview } from '@/types/workflowOverview';
import { OrgUpdatable } from '@/actions/organization';
import { WorkflowRunUpdatable } from '@/actions/workflowRun';

export const orgEndpoint: (orgId: string) => string = (orgId) => {
    return `api/org/${orgId}`;
};

export const appEndpoint: (orgId: string, appId: string) => string = (orgId, appId) => {
    const orgUrl = orgEndpoint(orgId);

    return `${orgUrl}/app/${appId}`;
};

export const releaseEndpoint: (orgId: string, appId: string, releaseId: string) => string = (
    orgId,
    appId,
    releaseId,
) => {
    const url = appEndpoint(orgId, appId);

    return `${url}/release/${releaseId}`;
};

export const appStateEndpoint: (orgId: string, appId: string) => string = (orgId, appId) => {
    const appUrl = appEndpoint(orgId, appId);

    return `${appUrl}`;
};

export const workflowEndpoint: (args: { orgId: string; workflowId: Nullable<string> }) => string = ({
    orgId,
    workflowId,
}) => {
    const url = orgEndpoint(orgId);

    const suffix = 'workflow';

    return workflowId ? `${url}/${suffix}/${workflowId}` : `${url}/${suffix}`;
};

export const workflowRunEndpoint: (args: {
    orgId: string;
    workflowId: string;
    workflowRunId: Nullable<string>;
}) => string = ({ orgId, workflowId, workflowRunId }) => {
    const url = workflowEndpoint({ orgId, workflowId });

    const suffix = 'workflowRun';

    return workflowRunId ? `${url}/${suffix}/${workflowRunId}` : `${url}/${suffix}`;
};

export const stepConfigEndpoint: (args: {
    orgId: string;
    workflowId: string;
    stepConfigId: Nullable<string>;
}) => string = ({ orgId, workflowId, stepConfigId }) => {
    const url = workflowEndpoint({ orgId, workflowId });

    const suffix = 'stepConfig';

    return stepConfigId ? `${url}/${suffix}/${stepConfigId}` : `${url}/${suffix}`;
};

export const checklistItemTemplateEndpoint: (args: {
    orgId: string;
    workflowId: string;
    stepConfigId: string;
    checklistItemId: Nullable<string>;
}) => string = ({ orgId, workflowId, stepConfigId, checklistItemId }) => {
    const url = stepConfigEndpoint({ orgId, workflowId, stepConfigId });
    const suffix = 'checklistItem';

    return checklistItemId ? `${url}/${suffix}/${checklistItemId}` : `${url}/${suffix}`;
};

export const automationEndpoint: (args: {
    orgId: string;
    workflowId: string;
    automationId: Nullable<string>;
}) => string = ({ orgId, workflowId, automationId }) => {
    const url = workflowEndpoint({ orgId, workflowId });
    const suffix = 'automation';

    return automationId ? `${url}/${suffix}/${automationId}` : `${url}/${suffix}`;
};

export const stepRunEndpoint: (args: {
    orgId: string;
    workflowId: string;
    workflowRunId: string;
    stepRunId: Nullable<string>;
}) => string = ({ orgId, workflowId, workflowRunId, stepRunId }) => {
    const url = workflowRunEndpoint({ orgId, workflowId, workflowRunId });

    const suffix = 'stepRun';

    return stepRunId ? `${url}/${suffix}/${stepRunId}` : `${url}/${suffix}`;
};

export const stepRunChecklistItemEndpoint: (args: {
    orgId: string;
    workflowId: string;
    workflowRunId: string;
    stepRunId: string;
    checklistItemId: Nullable<string>;
}) => string = ({ orgId, workflowId, workflowRunId, stepRunId, checklistItemId }) => {
    const url = stepRunEndpoint({ orgId, workflowId, workflowRunId, stepRunId });
    const suffix = 'checklistItem';

    return checklistItemId ? `${url}/${suffix}/${checklistItemId}` : `${url}/${suffix}`;
};

export const appSettingsApiRequestForAppUpdateType: (
    type: AppSettingsUpdatable,
) => (request: BaseAppRequest, auth: AuthCredentials) => Promise<AppSettings> = (type) => {
    switch (type) {
        case AppSettingsUpdatable.NewFeatureAffiliation:
            return API.addNewFeatureAffiliation;
        case AppSettingsUpdatable.DeleteFeatureAffiliation:
            return API.deleteFeatureAffiliation;
        case AppSettingsUpdatable.UpdateFeatureAffiliation:
            return API.updateFeatureAffiliationPattern;

        case AppSettingsUpdatable.NewReleaseBranchPattern:
            return API.createReleaseBranchPattern;

        case AppSettingsUpdatable.UpdateReleaseBranchPattern:
            return API.updateReleaseBranchPattern;

        case AppSettingsUpdatable.DeleteReleaseBranchPattern:
            return API.deleteReleaseBranchPattern;

        case AppSettingsUpdatable.NewReleaseTagPattern:
            return API.createReleaseTagPattern;

        case AppSettingsUpdatable.UpdateReleaseTagPattern:
            return API.updateReleaseTagPattern;

        case AppSettingsUpdatable.DeleteReleaseTagPattern:
            return API.deleteReleaseTagPattern;

        case AppSettingsUpdatable.BetaStyle:
            return API.releaseStepUpdateBetaStyle;

        case AppSettingsUpdatable.RegressionTestingStrategy:
            return API.releaseStepUpdateRegressionTestingStrategy;

        case AppSettingsUpdatable.SetAdditionalBranches:
            return API.updateAdditionalBranches;

        case AppSettingsUpdatable.UpdateAppleAppStoreReleaseDefaults:
            return API.updateAppleAppStoreReleaseDefaults;
        case AppSettingsUpdatable.UpdatePlayAppStoreReleaseDefaults:
            return API.updateGoogleAppStoreReleaseDefaults;
        case AppSettingsUpdatable.UpdateAppGalleryAppStoreReleaseDefaults:
            return API.updateHuaweiAppStoreReleaseDefaults;
        case AppSettingsUpdatable.UpdateAppStoreDefaultReleaseNotes:
            return API.updateAppStoreDefaultReleaseNotes;
        case AppSettingsUpdatable.UpdateReleaseScheduleCadenceSettings:
            return API.updateReleaseScheduleCadenceSettings;
        case AppSettingsUpdatable.UpdateReleasePilotList:
            return API.addReleasePilots;
        case AppSettingsUpdatable.RemoveReleasePilot:
            return API.removeReleasePilot;
        case AppSettingsUpdatable.UpdateVersioningDefaults:
            return API.updateVersioningDefaults;
        case AppSettingsUpdatable.UpdateNotificationSetting:
            return API.updateNotificationSettings;
        case AppSettingsUpdatable.BulkUpdateNotificationsEnabled:
            return API.bulkUpdateNotificationsEnabled;
        case AppSettingsUpdatable.UpdateBumpVersionOverrideFiles:
            return API.updateBumpVersionOverrideFiles;
        case AppSettingsUpdatable.UpdateTranslationDirectories:
            return API.updateTranslationDirectories;
        case AppSettingsUpdatable.UpdateNonBlockingSteps:
            return API.updateNonBlockingSteps;
        case AppSettingsUpdatable.UpdateFilterPaths:
            return API.updateFilterPaths;
        case AppSettingsUpdatable.UpdateFRAutoIgnoreTicketTypes:
            return API.updateFRAutoIgnoreTicketTypes;
        case AppSettingsUpdatable.TagCommit:
            return API.tagCommit;
        case AppSettingsUpdatable.UpdateUserRolesMap:
            return API.updateUserRolesMap;
        case AppSettingsUpdatable.TriggerIntegrationRefresh:
            return API.triggerIntegrationRefresh;
        case AppSettingsUpdatable.AddMonitoringMetric:
            return API.addMonitoringMetric;
        case AppSettingsUpdatable.DeleteMonitoringMetric:
            return API.deleteMonitoringMetric;
        case AppSettingsUpdatable.UpdateMonitoringMetric:
            return API.updateMonitoringMetric;
        case AppSettingsUpdatable.UpdateQualitativeOnboardingStatus:
            return API.updateQualitativeOnboardingStatus;
        case AppSettingsUpdatable.UpdateReleasePilotsOrder:
            return API.updateReleasePilotsOrder;
        case AppSettingsUpdatable.UpdateReleasePilotOverride:
            return API.updateReleasePilotOverride;
        case AppSettingsUpdatable.DeleteSigningKey:
            return API.deleteSigningKey;
        case AppSettingsUpdatable.UpdateSigningKey:
            return API.updateSigningKey;
        case AppSettingsUpdatable.RequiresFixRequestsApprovals:
            return API.updateFixesFeatureSettings;
        case AppSettingsUpdatable.UpdateBundleSizeDeviceModel:
            return API.updateBundleSizeDeviceModel;
        case AppSettingsUpdatable.UploadDefaultReviewAttachment:
            return API.uploadDefaultReviewAttachment;
        case AppSettingsUpdatable.RemoveDefaultReviewAttachment:
            return API.removeDefaultReviewAttachment;
    }
};

export const orgSettingsApiRequestForOrgUpdateType: (
    type: OrgSettingsUpdatable,
) => (
    request: BaseUpdateOrgSettingsRequest,
    auth: AuthCredentials,
) => Promise<
    OrganizationEntity | RunwayModelsResponse | API.DirectorySyncQueuedResponse | SetDefaultGroupsDisabledResponse
> = (type) => {
    switch (type) {
        case OrgSettingsUpdatable.MergeAccountsSetting:
            return API.updateMergeAccountsSetting;
        case OrgSettingsUpdatable.UpdateUser:
            return API.updateUser;
        case OrgSettingsUpdatable.RemoveOrgUser:
            return API.removeOrgUser;
        case OrgSettingsUpdatable.RemoveDeviceFromOrgUser:
            return API.removeUserDevice;
        case OrgSettingsUpdatable.NewWebhookEndpoint:
            return API.addNewWebhook;
        case OrgSettingsUpdatable.UpdateWebhookEndpoint:
            return API.updateWebhook;
        case OrgSettingsUpdatable.DeleteWebhookEndpoint:
            return API.deleteWebhook;
        case OrgSettingsUpdatable.UpdateAddNewUsersToAppsSetting:
            return API.updateAddNewUsersToAppsSetting;
        case OrgSettingsUpdatable.UpdateOrgTimezone:
            return API.updateOrgTimezone;
        case OrgSettingsUpdatable.UpdateOrgCalendarFirstDayOfWeek:
            return API.updateOrgCalendarFirstDayOfWeek;
        case OrgSettingsUpdatable.UpdateAISettings:
            return API.updateAISettings;
        case OrgSettingsUpdatable.DeletePublicApiKeyEndpoint:
            return API.deletePublicApiKey;
        case OrgSettingsUpdatable.UpdateUserGroupsForDirectorySyncGroup:
            return API.updateUserGroupsForDirectorySyncGroup;
        case OrgSettingsUpdatable.SetDisableDirectorySyncGroupMapping:
            return API.setDisableDirectorySyncGroupMapping;
        case OrgSettingsUpdatable.UpdateVCSUserNameForOrgUser:
            return API.updateOrgUserVCSUserName;
        case OrgSettingsUpdatable.UpdateTimezonePreferenceForOrgUser:
            return API.updateOrgUserTimezonePreference;
        case OrgSettingsUpdatable.UpdateTriageStateSortOrderForOrgUser:
            return API.updateOrgUserTriageStateSortOrder;
        case OrgSettingsUpdatable.CreateCustomGroup:
            return API.createCustomGroup;
        case OrgSettingsUpdatable.UpdateCustomGroup:
            return API.updateCustomGroup;
        case OrgSettingsUpdatable.DeleteCustomGroup:
            return API.deleteCustomGroup;
        case OrgSettingsUpdatable.SetDefaultGroupsDisabled:
            return API.setDefaultGroupsDisabled;
        case OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap:
            return API.updateUserGroupsToCommsGroupMapForProviderId;
        case OrgSettingsUpdatable.ActivateTrial:
            return API.activateSubscriptionTrial;
        case OrgSettingsUpdatable.UpdateIntegrationProviderName:
            return API.updateIntegrationProviderName;
    }
};

export const appSettingsV2ApiRequestForAppUpdateType: (
    type: AppSettingsV2Updatable,
) => (request: BaseAppRequest, auth: AuthCredentials) => Promise<AppSettingsV2APIResponse> = (type) => {
    switch (type) {
        case AppSettingsV2Updatable.NewChecklistItem:
            return API.releaseStepCreateChecklistItem;
        case AppSettingsV2Updatable.UpdateReleaseStepChecklistItem:
            return API.releaseStepUpdateChecklistItem;
        case AppSettingsV2Updatable.UpdateReleaseStepChecklistItemOrder:
            return API.updateChecklistItemsOrder;
        case AppSettingsV2Updatable.AddAppUsers:
            return API.addAppUsers;
        case AppSettingsV2Updatable.RemoveAppUsers:
            return API.removeAppUsers;
        case AppSettingsV2Updatable.DeleteReleaseStepChecklistItem:
            return API.releaseStepDeleteChecklistItem;
        case AppSettingsV2Updatable.EnableBuildDistro:
            return API.enableBuildDistro;
        case AppSettingsV2Updatable.UpdateAutomationSettings:
            return API.updateReleaseStepAutomationSettings;
        case AppSettingsV2Updatable.UpdateCustomMessagesTemplates:
            return API.updateCustomMessagesTemplates;
        case AppSettingsV2Updatable.RefreshIntegrationRefreshData:
            return API.refreshIntegrationRefreshData;
        case AppSettingsV2Updatable.UpdateStepNotificationConfigs:
            return API.updateStepNotificationConfigs;
        case AppSettingsV2Updatable.DeleteStepNotificationConfig:
            return API.deleteStepNotificationConfig;
        case AppSettingsV2Updatable.UpdatePilotRotationSettings:
            return API.updatePilotRotationSettings;
    }
};

export const fetchEntityRequestForEntityType: (
    action: TryFetchEntityAction,
) => (request: EntityRequest, auth: AuthCredentials, signal?: AbortSignal) => Promise<EntityResponse> = (action) => {
    switch (action.type) {
        case FetchableEntityType.AppState:
            return API.getAppState;
        case FetchableEntityType.AppHome:
            return API.getAppHome;
        case FetchableEntityType.Release:
            return API.getRelease;
        case FetchableEntityType.ReleaseStep:
            return API.getReleaseStep;
        case FetchableEntityType.ReleaseStats:
            return API.getReleaseStatsHistory;
        case FetchableEntityType.GetReleaseOverview:
            return API.getReleaseOverview;
        case FetchableEntityType.RolloutMonitoring:
            return API.getRolloutMonitoring;
        case FetchableEntityType.FeatureFlags:
            return API.getFeatureFlags;
        case FetchableEntityType.OrgOverviewV2:
            return API.getOrgOverviewV2;
        case FetchableEntityType.OrgSummaryView:
            return API.getOrgSummaryView;
        case FetchableEntityType.BuildDistributionBucketsWithRecentBuilds:
            return API.fetchBuildDistributionBucketsWithRecentBuilds;
        case FetchableEntityType.BuildDistributionBucket:
            return API.fetchBuildDistributionBucket;
        case FetchableEntityType.BuildDistributionBucketBuild:
            return API.fetchBuildDistributionBucketBuild;
        case FetchableEntityType.ProvisioningProfiles:
            return API.getProvisioningProfiles;
        case FetchableEntityType.BuildDistributionBucketDownloadMetrics:
            return API.fetchBuildDistributionBucketDownloadMetrics;
        case FetchableEntityType.OrgDestinationsAndVersions:
            return API.getOrgDestinationsAndVersions;
    }
};

export const fetchEntityRequestForEntityTypeV2: (
    action: TryFetchEntityActionV2,
) => (request: TryFetchEntityActionData<typeof action.type>, auth: AuthCredentials) => Promise<RunwayResponseV2> = (
    action,
) => {
    switch (action.type) {
        case FetchableEntityTypeV2.WorkflowOverview:
            return API.getWorkflowOverview;
        case FetchableEntityTypeV2.WorkflowRun:
            throw Error('Not implemented');
        case FetchableEntityTypeV2.Workflow:
            throw Error('Not implemented');
        case FetchableEntityTypeV2.StepConfig:
            return API.getStepConfig;
        case FetchableEntityTypeV2.StepRun:
            return API.getStepRun;
        case FetchableEntityTypeV2.StepRunsSummary:
            return API.getStepRunsSummary;
        case FetchableEntityTypeV2.OrgChecklistItemTemplates:
            return API.getChecklistItemOrgTemplates;
        case FetchableEntityTypeV2.OrgUsersVCSUserData:
            return API.getOrgUsersVCSUserData;
    }
};

export const updatableRequestForUpdatable: (
    updatableType: RunwayUpdatable,
) => (
    resourceIds: UpdatableResourceIdsType<typeof updatableType>,
    request: UpdatableRequestDataType<typeof updatableType>,
    auth: AuthCredentials,
) => UpdatableEndpointResponse = (updatableType) => {
    switch (updatableType) {
        // Step Run Updatables
        case StepRunUpdatable.EndBetaSoakPeriod:
            return API.endBetaSoak;
        case StepRunUpdatable.AssignBetaGroupsToBuild:
            return API.assignBetaGroupsToBuild;
        case StepRunUpdatable.CreateOrUpdateLocalizationNotesForBuild:
            return API.createOrUpdateLocalizationNotesForBuild;
        case StepRunUpdatable.SubmitBetaBuildToReview:
            return API.submitBetaBuildToReview;
        case StepRunUpdatable.AddIndividualTesterToBuild:
            return API.assignBetaTester;
        case StepRunUpdatable.PromoteBuildsToTestingTrack:
            return API.promoteBuildsToTestingTrack;
        case StepRunUpdatable.UpdateSelectedBuildsInAppStore:
            return API.updateSelectedBuildsInAppStore;
        case StepRunUpdatable.SubmitAppForReview:
            return API.submitAppForReview;
        case StepRunUpdatable.UpdateAppStoreSettings:
            return API.updateAppStoreSettings;
        case StepRunUpdatable.UpdateReviewDetails:
            return API.updateReviewDetails;
        case StepRunUpdatable.UploadReviewAttachment:
            return API.uploadReviewAttachmentForStepRun;
        case StepRunUpdatable.DeleteReviewAttachment:
            return API.deleteReviewAttachmentForStepRun;
        case StepRunUpdatable.RejectAppleSubmission:
            return API.rejectAppleSubmission;
        case StepRunUpdatable.ReleaseAppleVersion:
            return API.releaseAppleVersion;
        case StepRunUpdatable.KickoffRelease:
            return API.kickoffReleaseForStepRun;
        case StepRunUpdatable.CreateStepRunChecklistItem:
            return API.createStepRunChecklistItem;
        case StepRunUpdatable.UpdateStepRunChecklistItem:
            return API.updateStepRunChecklistItem;
        case StepRunUpdatable.DeleteStepRunChecklistItem:
            return API.deleteStepRunChecklistItem;
        case StepRunUpdatable.UpdateChecklistItemComment:
            return API.updateChecklistItemComment;
        case StepRunUpdatable.UpdateChecklistItemStatus:
            return API.updateChecklistItemStatus;
        case StepRunUpdatable.PingChecklistItem:
            return API.pingChecklistItem;
        case StepRunUpdatable.PingPendingChecklistItems:
            return API.pingPendingChecklistItems;
        case StepRunUpdatable.RejectVersion:
            return API.rejectVersion;
        case StepRunUpdatable.UpdateApplePhasedRelease:
        case StepRunUpdatable.UpdateGooglePhasedRelease:
        case StepRunUpdatable.UpdateHuaweiPhasedRelease:
        case StepRunUpdatable.UpdateExpoEASRollout:
        case StepRunUpdatable.PauseApplePhasedRelease:
        case StepRunUpdatable.PauseGooglePhasedRelease:
        case StepRunUpdatable.PauseHuaweiPhasedRelease:
        case StepRunUpdatable.ResumeApplePhasedRelease:
        case StepRunUpdatable.CompleteApplePhasedRelease:
        case StepRunUpdatable.CompleteGooglePhasedRelease:
        case StepRunUpdatable.CompleteHuaweiPhasedRelease:
            return API.updatePhasedRelease;
        case StepRunUpdatable.StartAutomatedAndroidStagedRollout:
            return API.startAutomatedAndroidStagedRollout;
        case StepRunUpdatable.TagRelease:
            return API.tagRelease;
        case StepRunUpdatable.ApproveMetadata:
            return API.approveMetadata;
        case StepRunUpdatable.UpdateMetadata:
            return API.updateMetadata;
        case StepRunUpdatable.DeleteScreenshot:
            return API.deleteScreenshot;
        case StepRunUpdatable.DeleteAppPreview:
            return API.deleteAppPreviewForStepRun;
        case StepRunUpdatable.ReorderScreenshots:
            return API.reorderScreenshots;
        case StepRunUpdatable.UpdateScreenshotsApproval:
            return API.updateScreenshotsApproval;
        case StepRunUpdatable.UpdateApprovalOverride:
            return API.updateApprovalOverride;
        case StepRunUpdatable.UpdateRegressionStatus:
            return API.updateRegressionTestingStatus;
        case StepRunUpdatable.AddReviewSubmissionItem:
            return API.addReviewSubmissionItem;
        case StepRunUpdatable.RemoveReviewSubmissionItem:
            return API.removeReviewSubmissionItem;
        case StepRunUpdatable.UnblockReviewSubmission:
            return API.unblockReviewSubmission;
        case StepRunUpdatable.CancelReviewSubmission:
            return API.cancelReviewSubmission;
        case StepRunUpdatable.TriggerBuildSystemWorkflow:
            return API.triggerBuildSystemWorkflow;
        case StepRunUpdatable.RetryArtifactUpload:
            return API.retryArtifactUpload;
        case StepRunUpdatable.RetryDsymUpload:
            return API.retryDsymUploadForStepRun;
        case StepRunUpdatable.LockBuild:
            return API.lockBuild;
        case StepRunUpdatable.UnlockBuild:
            return API.unlockBuild;
        case StepRunUpdatable.OverrideCIFailure:
            return API.overrideCiFailure;
        case StepRunUpdatable.StartResignSequence:
            return API.startResignSequenceForStepRun;
        // StepConfig Updatables
        case StepConfigUpdatable.UpdateBetaTestingStyle:
            return API.updateBetaTestingStyle;
        case StepConfigUpdatable.UpdateRegressionTestingStrategy:
            return API.updateRegressionTestingStrategy;
        case StepConfigUpdatable.CreateChecklistItemTemplate:
            return API.createChecklistItemTemplate;
        case StepConfigUpdatable.UpdateChecklistItemTemplate:
            return API.updateChecklistItemTemplate;
        case StepConfigUpdatable.DeleteChecklistItemTemplate:
            return API.deleteChecklistItemTemplate;
        case StepConfigUpdatable.UpdateChecklistItemTemplateOrder:
            return API.updateChecklistItemTemplateOrder;
        case StepConfigUpdatable.UpdateStepConfig:
            return API.updateStepConfig;
        case StepConfigUpdatable.DeleteStepConfig:
            return API.deleteStepConfig;
        case WorkflowUpdatable.UpdateStepGraph:
            return API.updateStepGraph;
        case WorkflowUpdatable.RenameStepArchetypeGroup:
            return API.renameStepArchetypeGroup;
        // WorkflowRun Updatables
        case WorkflowRunUpdatable.TriggerBatchAction:
            return API.triggerBatchAction;
        case WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup:
            return API.pingPendingChecklistItemsForStepGroup;
        case WorkflowRunUpdatable.BulkMarkCLIsAsCompleted:
            return API.bulkMarkCLIsAsCompleted;
        case WorkflowRunUpdatable.UpdateDestinationStatuses:
            return API.updateDestinationStatuses;
        // Destination Updatables
        case DestinationUpdatable.UpdateDestinationDetails:
            return API.updateDestinationDetails;
        // Workflow Updatables
        case WorkflowUpdatable.UpdateAutomationSettings:
            return API.updateAutomationSettings;
        case WorkflowUpdatable.CreateStepConfig:
            return API.createStepConfig;
        // Org Updatables
        case OrgUpdatable.CreateOrgChecklistItemTemplate:
            return API.createChecklistItemOrgTemplate;
        case OrgUpdatable.UpdateOrgChecklistItemTemplate:
            return API.updateChecklistItemOrgTemplate;
        case OrgUpdatable.DeleteOrgChecklistItemTemplate:
            return API.deleteChecklistItemOrgTemplate;
        case OrgUpdatable.CreateOrgTag:
            return API.createOrgTag;
        case OrgUpdatable.UpdateOrgTag:
            return API.updateOrgTag;
        case OrgUpdatable.DeleteOrgTag:
            return API.deleteOrgTag;
        default:
            throw Error('Unrecognized updatable type.');
    }
};

export const runwayModelsFromFetchEntityResponse: (
    action: TryFetchEntityActionV2,
    response: RunwayResponseV2,
) => RunwayModelsResponse = (action, response) => {
    switch (action.type) {
        case FetchableEntityTypeV2.WorkflowRun:
            throw Error('Not implemented');
        case FetchableEntityTypeV2.Workflow:
            throw Error('Not implemented');
        case FetchableEntityTypeV2.WorkflowOverview:
            return {
                workflowOverview: response.data as WorkflowOverview,
            } satisfies RunwayModelsResponse;
        case FetchableEntityTypeV2.StepConfig:
            return {
                stepConfigs: [response.data as StepConfig],
            } satisfies RunwayModelsResponse;
        case FetchableEntityTypeV2.StepRunsSummary:
            return response.data as RunwayModelsResponse;
        case FetchableEntityTypeV2.StepRun:
            return {
                stepRuns: [response.data as StepRun],
            } satisfies RunwayModelsResponse;
        case FetchableEntityTypeV2.OrgChecklistItemTemplates:
            return {
                orgChecklistItemTemplates: response.data as ChecklistItemTemplate[],
            } satisfies RunwayModelsResponse;
        case FetchableEntityTypeV2.OrgUsersVCSUserData:
            return {
                orgUsersVCSUserData: response.data as OrgUsersVCSUserData,
            } satisfies RunwayModelsResponse;
    }
};

export const fetchPaginatedEntitiesRequestForEntityType: (action: TryFetchPaginatedEntitiesAction) => (
    request: PaginatedEntitiesRequest,
    limit: number,
    // Offset-paginated entities pass a number; cursor-paginated ones pass a string.
    pageParam: number | string,
    auth: AuthCredentials,
) => Promise<PaginatedEntitiesResponse> = (action) => {
    switch (action.type) {
        case FetchablePaginatedEntityType.Releases:
            return API.getCompletedReleases;
        case FetchablePaginatedEntityType.BuildDistributionBucketBuilds:
            return API.fetchBuildDistributionBucketBuilds;
        case FetchablePaginatedEntityType.BuildDistributionSharedWithMeBuilds:
            return API.fetchSharedWithMeBuildDistroBuilds;
        case FetchablePaginatedEntityType.BuildDistributionBuildWorkItemsTimeline:
            return API.fetchBuildDistributionBuildWorkItemsTimeline;
        case FetchablePaginatedEntityType.AllDevices:
            return API.getAllDevices;
        case FetchablePaginatedEntityType.Issues:
            return API.getIssues;
    }
};

export const fetchEntityUpdateAction: (action: TryFetchEntityAction, response: any) => FetchEntitySuccessAction = (
    action,
    response,
) => {
    switch (action.type) {
        case FetchableEntityType.AppState: {
            const appStateResponse = response as AppStateResponse;
            return fetchAppStateSuccess(appStateResponse);
        }
        case FetchableEntityType.AppHome: {
            const appHomeRequest = action.payload.data as AppEntityRequest;
            const payload: AppHomeResponse = {
                appId: appHomeRequest.appId,
                appHome: response as AppHome,
            };
            return fetchAppHomeSuccess(payload);
        }
        case FetchableEntityType.Release: {
            const releaseRequest = action.payload.data as ReleaseEntityRequest;
            const payload: FetchReleaseResponse = {
                releaseId: releaseRequest.releaseId,
                release: response as Release,
            };
            return fetchReleaseSuccess(payload);
        }
        case FetchableEntityType.ReleaseStep: {
            const request = action.payload.data as ReleaseStepEntityRequest;
            const releaseStep = response as ReleaseStep;

            return fetchReleaseStepSuccess({
                ...request,
                releaseStep,
            });
        }
        case FetchableEntityType.ReleaseStats: {
            const request = action.payload.data as ReleaseEntityRequest;
            const payload: ReleaseStatsHistoryResponse = {
                appId: request.appId,
                orgId: request.orgId,
                releaseId: request.releaseId,
                statistics: response.statistics as ReleaseStatistics[],
            };
            return fetchReleaseStatsHistorySuccess(payload);
        }
        case FetchableEntityType.GetReleaseOverview: {
            const request = action.payload.data as ReleaseEntityRequest;
            const { statistics, ...rest } = response as ReleaseOverviewResponse;
            const payload: ReleaseOverviewResponse = {
                appId: request.appId,
                orgId: request.orgId,
                releaseId: request.releaseId,
                statistics,
                ...rest,
            };
            return fetchReleaseOverviewSuccess(payload);
        }
        case FetchableEntityType.RolloutMonitoring: {
            const request = action.payload.data as ReleaseEntityRequest;
            const payload: RolloutMonitoringResponse = {
                appId: request.appId,
                orgId: request.orgId,
                releaseId: request.releaseId,
                rolloutMonitoring: response as RolloutMonitoring,
            };
            return fetchRolloutMonitoringSuccess(payload);
        }
        case FetchableEntityType.FeatureFlags: {
            const request = action.payload.data as ReleaseEntityRequest;
            const payload: FetchFeatureFlagsResponse = {
                releaseId: request.releaseId,
                featureFlags: response as FeatureFlag[],
            };
            return fetchFeatureFlagsSuccess(payload);
        }
        case FetchableEntityType.OrgOverviewV2: {
            const typedResponse = response as OrgOverviewV2Response;

            const request = action.payload.data as OrgOverviewEntityRequest<OrgOverviewFilters>;

            const responsePayload: OrgOverviewV2Response = {
                id: action.payload.id as string,
                singleAppId: request.filters?.appId,
                statistics: typedResponse.statistics,
                liveRelease: typedResponse.liveRelease,
                nextRelease: typedResponse.nextRelease,
                averageApprovalItemDurations: typedResponse.averageApprovalItemDurations,
            };

            return fetchOrgOverviewV2Success(responsePayload);
        }
        case FetchableEntityType.BuildDistributionBucketsWithRecentBuilds: {
            const bucketsAndBuilds = response as FetchBuildDistributionBucketsWithRecentBuildsResponse;

            const request = action.payload.data as BuildDistributionBucketsWithRecentBuildsEntityRequest;

            return fetchBuildDistributionBucketsWithRecentBuildsSuccess({
                appId: request.appId,
                orgId: request.orgId,
                bucketsAndBuilds,
            });
        }
        case FetchableEntityType.BuildDistributionBucket: {
            const bucket = response as FetchBuildDistributionBucketResponse;
            const request = action.payload.data as BuildDistributionBucketsWithRecentBuildsEntityRequest;

            return fetchBuildDistributionBucketSuccess({
                appId: request.appId,
                orgId: request.orgId,
                bucket,
            });
        }
        case FetchableEntityType.BuildDistributionBucketBuild: {
            const build = response as FetchBuildDistributionBucketBuildResponse;
            const request = action.payload.data as BuildDistributionBucketsWithRecentBuildsEntityRequest;
            return fetchBuildDistributionBucketBuildSuccess({
                appId: request.appId,
                orgId: request.orgId,
                build,
            });
        }
        case FetchableEntityType.ProvisioningProfiles: {
            const typedResponse = response as GetProvisioningProfilesResult;
            const request = action.payload.data as AppEntityRequest;

            return fetchProvisioningProfilesSuccess({
                appId: request.appId,
                orgId: request.orgId,
                provisioningProfiles: typedResponse.profiles,
            });
        }
        case FetchableEntityType.BuildDistributionBucketDownloadMetrics: {
            const metrics = response as FetchBuildDistributionBucketDownloadMetricsResponse;
            const request = action.payload.data as BuildDistributionBucketDownloadMetricRequest;
            return fetchBuildDistributionBucketDownloadMetricsSuccess({
                appId: request.appId,
                orgId: request.orgId,
                bucketId: request.bucketId,
                metrics,
            });
        }
        case FetchableEntityType.OrgSummaryView: {
            const typedResponse = response as RunwayResponseV2<SummaryViewResponseAPI>;
            const summaryView = typedResponse.data as SummaryViewResponseAPI;
            const request = action.payload.data as OrgSummaryViewEntityRequest;

            const responsePayload: OrgSummaryViewResponse = {
                requestId: action.payload.id as string,
                fetchedAt: Date.now(),
                scopeKey: createSummaryViewScopeKey({
                    requestId: action.payload.id as string,
                    appToDestinationIds: request.appToDestinationIds,
                    keywords: request.keywords,
                    offset: request.offset,
                    limit: request.limit,
                }),
                rowSetSignature: createSummaryViewScopeKey({
                    requestId: action.payload.id as string,
                    appToDestinationIds: request.appToDestinationIds,
                    versions: request.versions,
                    keywords: request.keywords,
                    offset: request.offset,
                    limit: request.limit,
                }),
                fetchedProperties: request.properties,
                summaryView,
            };

            return fetchOrgSummaryViewSuccess(responsePayload);
        }
        case FetchableEntityType.OrgDestinationsAndVersions: {
            const typedResponse = response as RunwayResponseV2<OrgDestinationsResponse>;
            const request = action.payload.data as { orgId: string };
            const data = typedResponse.data as OrgDestinationsResponse;

            const responsePayload: OrgDestinationsAndVersionsResponse = {
                orgId: request.orgId,
                data,
            };

            return fetchOrgDestinationsAndVersionsSuccess(responsePayload);
        }
    }
};

export const fetchPaginatedEntitiesUpdateAction: (
    action: TryFetchPaginatedEntitiesAction,
    response: PaginatedEntitiesResponse,
) => FetchPaginatedEntitiesSuccessAction = (action, response) => {
    switch (action.type) {
        case FetchablePaginatedEntityType.Releases: {
            const request = action.payload.data as ReleasesRequest;

            const connectedReleases: ConnectedReleasesResponse = {
                appId: request.appId,
                orgId: request.orgId,
                orgIdParam: request.orgIdParam,
                appIdParam: request.appIdParam,
                releases: (response as ReleasesResponse).releases,
            };

            return fetchReleasesSuccessAction(connectedReleases);
        }
        case FetchablePaginatedEntityType.BuildDistributionBucketBuilds: {
            const typedResponse = response as FetchBuildDistributionBucketBuildsResponse;
            const request = action.payload.data as BuildDistributionBucketsWithRecentBuildsEntityRequest;

            return fetchBuildDistributionBucketBuildsSuccess({
                bucketId: request.bucketId,
                appId: request.appId,
                orgId: request.orgId,
                builds: typedResponse.builds,
            });
        }
        case FetchablePaginatedEntityType.BuildDistributionSharedWithMeBuilds: {
            const typedResponse = response as FetchBuildDistributionSharedWithMeBuildsResponse;
            const request = action.payload.data as BuildDistributionSharedWithMeBuildsEntityRequest;

            return fetchBuildDistributionSharedWithMeBuildsSuccess({
                appId: request.appId,
                orgId: request.orgId,
                ...typedResponse,
            });
        }
        case FetchablePaginatedEntityType.BuildDistributionBuildWorkItemsTimeline: {
            const typedResponse = response as FetchBuildDistributionBucketBuildsWorkItemsTimelineResponse;
            const request = action.payload.data as BuildDistributionBucketBuildsWorkItemsTimelineRequest;

            return fetchBuildDistributionBuildWorkItemsTimelineSuccess({
                appId: request.appId,
                orgId: request.orgId,
                buildId: request.buildId,
                ...typedResponse,
            });
        }
        case FetchablePaginatedEntityType.AllDevices: {
            const typedResponse = response as GetAllDevicesResponse;
            const request = action.payload.data as GetAllDevicesRequest;

            return fetchAllDevicesSuccess({
                appId: request.appId,
                orgId: request.orgId,
                ...typedResponse,
            });
        }
        case FetchablePaginatedEntityType.Issues: {
            const typedResponse = response as IssuesResponse;
            const request = action.payload.data as IssuesRequest;

            return fetchIssuesSuccessAction({
                appId: request.appId,
                orgId: request.orgId,
                issues: typedResponse.issues,
                offset: action.payload.offset,
                openCount: typedResponse.openCount,
                closedCount: typedResponse.closedCount,
                defaultStateSortOrder: typedResponse.defaultStateSortOrder,
            });
        }
    }
};

export const fetchPaginatedEntitiesUpdatePaginationDataAction: (
    action: TryFetchPaginatedEntitiesAction,
    response: PaginatedEntitiesResponse,
) => FetchPaginatedEntitiesUpdatePaginationDataAction = (action, response) => {
    switch (action.type) {
        case FetchablePaginatedEntityType.Releases: {
            const typedResponse = response as ReleasesResponse;

            return updateReleasesPagination({
                appId: action.payload.data.appId,
                limit: action.payload.limit,
                offset: action.payload.offset,
                currentCount: typedResponse.releases.length,
                totalCount: typedResponse.totalCount,
            });
        }
        case FetchablePaginatedEntityType.BuildDistributionBucketBuilds: {
            const typedResponse = response as FetchBuildDistributionBucketBuildsResponse;

            return updateBucketBuildsPagination({
                bucketId: action.payload.id as string,
                limit: action.payload.limit,
                nextCursor: typedResponse.nextCursor,
            });
        }
        case FetchablePaginatedEntityType.BuildDistributionSharedWithMeBuilds: {
            const typedResponse = response as FetchBuildDistributionSharedWithMeBuildsResponse;
            const currentCount = typedResponse.builds.length;
            const totalCount = typedResponse.totalCount;

            return updateSharedWithMeBuildsPagination({
                appId: action.payload.data.appId,
                limit: action.payload.limit,
                offset: action.payload.offset,
                currentCount,
                totalCount,
            });
        }
        case FetchablePaginatedEntityType.BuildDistributionBuildWorkItemsTimeline: {
            const typedResponse = response as FetchBuildDistributionBucketBuildsWorkItemsTimelineResponse;
            const currentCount = typedResponse.items.length;
            const totalCount = typedResponse.total;
            const data = action.payload.data as BuildDistributionBucketBuildsWorkItemsTimelineRequest;
            return updateBucketBuildWorkItemsTimelinePagination({
                appId: data.appId,
                buildId: data.buildId,
                bucketId: data.bucketId,
                limit: action.payload.limit,
                offset: action.payload.offset,
                currentCount,
                totalCount,
            });
        }
        case FetchablePaginatedEntityType.AllDevices: {
            const typedResponse = response as GetAllDevicesResponse;
            const currentCount = typedResponse.devices.length;
            const totalCount = typedResponse.total;
            const data = action.payload.data as GetAllDevicesRequest;
            return updateAllDevicesPagination({
                appId: data.appId,
                limit: action.payload.limit,
                offset: action.payload.offset,
                currentCount,
                totalCount,
            });
        }
        case FetchablePaginatedEntityType.Issues: {
            const issuesCount = (response as IssuesResponse).issues.length;
            return updateIssuesPagination({
                appId: action.payload.data.appId,
                limit: action.payload.limit,
                offset: action.payload.offset,
                currentCount: issuesCount,
            });
        }
    }
};

export const updateReleaseStepApiRequestForReleaseStepUpdatableType: (
    type: ReleaseStepUpdatable,
) => (
    resourceIds: UpdateReleaseStepResourceIds<typeof type>,
    request: UpdateReleaseStepRequestDataTypes<typeof type>,
    auth: AuthCredentials,
) => Promise<RunwayModelsResponse> = (type) => {
    switch (type) {
        case ReleaseStepUpdatable.IgnoreFRItem:
        case ReleaseStepUpdatable.UnignoreFRItem: {
            return API.ignoreUnignoreFRItem;
        }
        case ReleaseStepUpdatable.AddFixRequest: {
            return API.addFixRequest;
        }
        case ReleaseStepUpdatable.UpdateFixRequest: {
            return API.updateFixRequest;
        }
        case ReleaseStepUpdatable.ApproveFixRequest: {
            return API.approveFixRequest;
        }
        case ReleaseStepUpdatable.RejectFixRequest: {
            return API.rejectFixRequest;
        }
        case ReleaseStepUpdatable.MoveFixRequestToPendingApproval: {
            return API.moveFixRequestToPendingApproval;
        }
        case ReleaseStepUpdatable.TranslationsApproval:
            return API.updateTranslationsApproval;
        case ReleaseStepUpdatable.UploadTranslationStrings:
            return API.uploadTranslations;
        case ReleaseStepUpdatable.ExportTranslationStrings:
            return API.exportTranslations;
        case ReleaseStepUpdatable.SyncTranslationStrings:
            return API.syncTranslations;
        default:
            throw Error('Unrecognized update release step type.');
    }
};

export const updateApiForChecklistItemUpdatable: (
    action: TryUpdateChecklistItemAction,
) => (request: BaseUpdateReleaseRequest, auth: AuthCredentials) => Promise<ChecklistItemUpdatableAPIResponse> = (
    action,
) => {
    switch (action.type) {
        case ChecklistItemUpdatable.ChecklistItemStatusChanged:
            return API.releaseStepUpdateChecklistItemStatus;
        case ChecklistItemUpdatable.ChecklistItemCommentChanged:
            return API.releaseStepUpdateChecklistItemComment;
        default:
            throw Error('Unrecognized update checklist item type.');
    }
};

export const updateReleaseApiRequestForReleaseUpdatable: (
    action: TryUpdateReleaseAction,
) => (request: BaseUpdateReleaseRequest, auth: AuthCredentials) => Promise<Release> = (action) => {
    switch (action.type) {
        case ReleaseUpdatable.RegressionStatus: {
            return API.updateRegressionStatusForReleaseStep;
        }
        case ReleaseUpdatable.ScreenshotsApproval: {
            return API.updateScreenshotsApprovalForReleaseStep;
        }
        case ReleaseUpdatable.DeleteScreenshot: {
            return API.deleteScreenshotForReleaseStep;
        }
        case ReleaseUpdatable.DeleteAppPreview: {
            return API.deleteAppPreview;
        }
        case ReleaseUpdatable.MetadataApproval: {
            return API.updateMetadataApproval;
        }
        case ReleaseUpdatable.EndBetaSoak: {
            return API.releaseStepEndBetaSoak;
        }
        case ReleaseUpdatable.TagRelease:
        case ReleaseUpdatable.UpdateReleaseTag: {
            return API.tagReleaseReleaseStep;
        }
        case ReleaseUpdatable.UpdateReleaseNotes: {
            return API.updateReleaseNotes;
        }
        case ReleaseUpdatable.UploadMetadataForTranslation: {
            return API.uploadMetadataForTranslation;
        }
        case ReleaseUpdatable.ExportMetadataTranslations: {
            return API.exportMetadataTranslations;
        }
        case ReleaseUpdatable.SyncMetadataTranslations: {
            return API.syncMetadataTranslations;
        }
        case ReleaseUpdatable.UpdatePlanningSummary: {
            return API.updateReleasePlanningSummary;
        }
        case ReleaseUpdatable.UpdateSelectedBuild: {
            return API.updateSelectedBuild;
        }
        case ReleaseUpdatable.UpdateAppleAppStoreSettings: {
            return API.updateAppleAppStoreSettings;
        }
        case ReleaseUpdatable.UpdateGoogleReleaseSettings: {
            return API.updateGoogleReleaseSettings;
        }
        case ReleaseUpdatable.UpdateHuaweiReleaseSettings: {
            return API.updateHuaweiReleaseSettings;
        }
        case ReleaseUpdatable.SubmitAppForReview: {
            return API.submitAppForReviewReleaseStep;
        }
        case ReleaseUpdatable.HaltGooglePhasedRelease:
        case ReleaseUpdatable.UpdateGooglePhasedRelease:
        case ReleaseUpdatable.CompleteGooglePhasedRelease: {
            return API.updateGooglePhasedRelease;
        }
        case ReleaseUpdatable.PauseHuaweiPhasedRelease:
        case ReleaseUpdatable.UpdateHuaweiPhasedRelease:
        case ReleaseUpdatable.CompleteHuaweiPhasedRelease:
            return API.updateHuaweiPhasedRelease;
        case ReleaseUpdatable.PauseApplePhasedRelease:
        case ReleaseUpdatable.ResumeApplePhasedRelease:
        case ReleaseUpdatable.CompleteApplePhasedRelease: {
            return API.updateApplePhasedRelease;
        }
        case ReleaseUpdatable.RejectAppleSubmission: {
            return API.rejectAppleSubmissionForReleaseStep;
        }
        case ReleaseUpdatable.ReleaseAppleVersion: {
            return API.releaseAppleVersionForReleaseStep;
        }
        case ReleaseUpdatable.CreateReleaseBranch: {
            return API.createReleaseBranch;
        }
        case ReleaseUpdatable.PromoteWorkingBranch: {
            return API.promoteBranch;
        }
        case ReleaseUpdatable.BumpVersion: {
            return API.bumpVersion;
        }
        case ReleaseUpdatable.SelectReleaseCandidateBuild: {
            return API.selectReleaseCandidateBuild;
        }
        case ReleaseUpdatable.UpdateBetaBuildLocalization: {
            return API.updateBetaBuildNotes;
        }
        case ReleaseUpdatable.AddBetaBuildLocalizedNotes: {
            return API.addBetaNotesForBetaBuild;
        }
        case ReleaseUpdatable.AddIndividualTesterToBuild: {
            return API.addIndividualTesterToBuild;
        }
        case ReleaseUpdatable.AddBetaGroupsToBuild: {
            return API.addBetaGroupToBuild;
        }
        case ReleaseUpdatable.SubmitBetaBuildForReview: {
            return API.submitBetaBuildForReview;
        }
        case ReleaseUpdatable.UpdateMultiLocaleTestingNotes: {
            return API.updateMultiLocaleTestingNotes;
        }
        case ReleaseUpdatable.PromoteActiveBuildToTestingTrack:
        case ReleaseUpdatable.PromoteBuildToTestingTrack: {
            return API.promoteBuildToTestingTrack;
        }
        case ReleaseUpdatable.PauseScheduleCadence: {
            return API.pauseScheduleCadence;
        }
        case ReleaseUpdatable.ResumeScheduleCadence: {
            return API.resumeScheduleCadence;
        }
        case ReleaseUpdatable.ResumeAutoSelectingLatestReleaseCandidateBuild: {
            return API.resumeAutoSelectLatestReleaseCandidateBuild;
        }
        case ReleaseUpdatable.StartAutomatedStagedRollout: {
            return API.startAutomatedStagedRollout;
        }
        case ReleaseUpdatable.RetryAppStoreArtifactUpload:
            return API.retryAppStoreArtifactUploadForReleaseStep;
        case ReleaseUpdatable.RetryDsymUpload:
            return API.retryDsymUpload;
        case ReleaseUpdatable.UpdateReviewDetails:
            return API.updateReviewDetailsForReleaseStep;
        case ReleaseUpdatable.UploadReviewAttachment:
            return API.uploadReviewAttachmentForReleaseStep;
        case ReleaseUpdatable.DeleteReviewAttachment:
            return API.deleteReviewAttachmentForReleaseStep;
        case ReleaseUpdatable.MarkReleaseSkipped:
            return API.skipRelease;
        case ReleaseUpdatable.MarkReleaseComplete:
            return API.completeRelease;
        case ReleaseUpdatable.MarkReleaseUncomplete:
            return API.uncompleteRelease;
        case ReleaseUpdatable.MarkReleaseUnskipped:
            return API.unskipRelease;
        case ReleaseUpdatable.TryStartResignSequence:
            return API.startResignSequence;
        case ReleaseUpdatable.CustomizeReleaseSummaryMessage:
            return API.updateReleaseSummaryMessage;
        case ReleaseUpdatable.AddReviewSubmissionItem:
            return API.addReviewSubmissionItemForReleaseStep;
        case ReleaseUpdatable.RemoveReviewSubmissionItem:
            return API.removeReviewSubmissionItemForReleaseStep;
        case ReleaseUpdatable.UnblockReleaseReviewSubmission:
            return API.unblockReleaseReviewSubmissionForReleaseStep;
        case ReleaseUpdatable.CancelReleaseReviewSubmission:
            return API.cancelReleaseReviewSubmissionForReleaseStep;
        default:
            throw Error('Unrecognized update release type.');
    }
};

export const updateFeatureFlagsRequestForFeatureFlagUpdatable: (
    action: TryUpdateFeatureFlagsAction,
) => (request: BaseUpdateReleaseRequest, auth: AuthCredentials) => Promise<FeatureFlag> = (action) => {
    switch (action.type) {
        case FeatureFlagUpdatable.ToggleFeatureFlag: {
            return API.toggleFeatureFlag;
        }
        default:
            throw Error('Unrecognized update release type.');
    }
};

export const updateBuildDistributionBucketRequestForAction: (
    action: TryUpdateBuildDistributionBucketAction,
) => (request: EntityRequest, auth: AuthCredentials) => Promise<BuildDistroBucketBase> = (action) => {
    switch (action.type) {
        case BuildDistributionUpdatable.AddBuildDistroBucketMembers: {
            return API.addBuildDistroBucketMembers;
        }
        case BuildDistributionUpdatable.RemoveBuildDistroBucketMember: {
            return API.removeBuildDistroBucketMember;
        }
        case BuildDistributionUpdatable.UpdateBuildDistroBucketMemberPermissions: {
            return API.updateBuildDistroBucketMemberPermissions;
        }
        case BuildDistributionUpdatable.ToggleBuildDistroBucketNotifications: {
            return API.toggleBuildDistroBucketNotifications;
        }
        case BuildDistributionUpdatable.UpdateBuildDistroBucketNotificationSettings: {
            return API.updateBuildDistroBucketNotificationSettings;
        }
        case BuildDistributionUpdatable.UpdateBuildDistroBucketAutomationSettings: {
            return API.updateBuildDistroBucketAutomationSettings;
        }
        case BuildDistributionUpdatable.UpdateBuildDistroBucket: {
            return API.updateBuildDistributionBucket;
        }
    }
};

export const updateDevicesRequestForAction: (
    action: TryUpdateDevicesAction,
) => (request: EntityRequest, auth: AuthCredentials) => Promise<GetAllDevicesResponse> = (action) => {
    switch (action.type) {
        case DevicesUpdatable.RegisterDevices: {
            return API.bulkRegisterDevices;
        }
        case DevicesUpdatable.DisableEnableDevices: {
            return API.bulkEnableDisableDevices;
        }
        case DevicesUpdatable.DeleteDevices: {
            return API.bulkDeleteDevices;
        }
        case DevicesUpdatable.AddDevice: {
            return API.addDevice;
        }
    }
};
